import { flip, offset, shift } from '@floating-ui/react-dom';
import { RefreshCcw } from '@styled-icons/feather';
import { ImageElement } from '@typedream/core';
import { toDOMNode } from '@udecode/plate-core';
import classNames from 'classnames';
import { memo, useEffect, useState } from 'react';
import { Floating } from '../../../components/Floating';
import { SelectMedia } from '../../../components/SelectMedia';
import { OverlayPortal } from '../../../overlay/OverlayPortal';
import { useTPlateEditorRef } from '../../../plate';
import { useSelectedElementEntry } from '../../../plugins/elementSelection';
import { changeImage } from '../../../nodes/image';
import { getImageDimension } from '../../../utils/image';
import styles from '../SideToolbar.module.css';

interface SelectImageProps {
  reference: HTMLElement
  defaultValue?: string
  onSelect: (image: { url: string; width?: number; height?: number }) => void
}

function SelectImage(props: SelectImageProps) {
  const { reference, onSelect, defaultValue } = props;
  const editor = useTPlateEditorRef();
  const [loading, setLoading] = useState(false);

  const onSelectImageUrl = async (url: string) => {
    setLoading(true);
    try {
      const dimension = await getImageDimension(url);
      onSelect({ url, ...dimension });
    } catch (e) {
      onSelect({ url });
    } finally {
      setLoading(false);
    }
  };

  // prevent weird initial transition from position 0,0 to its supposed position
  const [isTransitionApplied, setIsTransitionApplied] = useState(false);
  useEffect(() => {
    const timeout = setTimeout(() => setIsTransitionApplied(true), 0);
    return () => clearTimeout(timeout);
  }, []);

  return (
    <OverlayPortal>
      <Floating
        reference={reference}
        options={{
          placement: 'bottom',
          middleware: [
            offset(-6),
            flip(),
            shift({
              crossAxis: true,
              padding: { top: 8, bottom: 8 },
            }),
          ],
        }}
        autoUpdate
        className={styles.box}
        style={{ transition: isTransitionApplied ? 'transform 0.3s' : undefined }}
      >
        <SelectMedia
          tabs={{
            upload: {
              accept: 'image/*',
              button: 'Choose an image',
              onSelect: async (file) => {
                if (!editor.fileManager) return;

                setLoading(true);

                try {
                  const url = await editor.fileManager.upload(file);
                  onSelectImageUrl(url);
                } catch (e) {
                  setLoading(false);
                }
              },
              disabled: loading,
            },
            link: {
              button: 'Use this image',
              placeholder: 'Enter Image URL...',
              defaultValue,
              onSelect: onSelectImageUrl,
              disabled: loading,
            },
            resources: {
              onSelect: onSelectImageUrl,
            },
          }}
        />
      </Floating>
    </OverlayPortal>
  );
}

SelectImage.defaultProps = {
  defaultValue: undefined,
};

function ImageSource() {
  const editor = useTPlateEditorRef();
  const selectedElementEntry = useSelectedElementEntry<ImageElement>();
  const [isReplacingImage, setIsReplacingImage] = useState(false);

  useEffect(() => setIsReplacingImage(false), [selectedElementEntry]);

  useEffect(() => {
    const handler = () => setIsReplacingImage((v) => !v);
    editor.emitter.on('toolbar:toggle-replace-image', handler);
    return () => editor.emitter.off('toolbar:toggle-replace-image', handler);
  }, [editor.emitter]);

  if (!selectedElementEntry) return null;
  const [element, path] = selectedElementEntry;
  const domNode = toDOMNode(editor, element);

  return (
    <div className={styles.section}>
      <span className={styles.label}>Image Source</span>
      <button
        type="button"
        className={classNames(styles.button)}
        onClick={() => setIsReplacingImage((v) => !v)}
      >
        <RefreshCcw size={16} className="mr-2" />
        <span>Replace Image</span>
      </button>

      {isReplacingImage && domNode && (
        <SelectImage
          reference={domNode}
          defaultValue={element.url}
          onSelect={(image) => {
            if (path) {
              changeImage(editor, image, { at: path });
            }
          }}
        />
      )}
    </div>
  );
}

export default memo(ImageSource);
